import {useCallback, useEffect} from "react";
import {batch, useComputed, useSignal} from "@preact/signals-react";

import {BACKEND_APIS, GROUPED_BY} from "@constants";
import {isBannersPercentageDynamicLoading} from "../@signals";
import useBannerPercentageSearch from "./useSearch";


export default function useBannerPercentageDynamic() {
    const totalKey = useSignal(GROUPED_BY.RETAILER)
    const shareKey = useSignal(GROUPED_BY.BRAND)
    const forceLoad = useSignal(false)
    const data = useSignal([])

    const excludeOptions = useComputed(() => {
        if ([GROUPED_BY.RETAILER, GROUPED_BY.COUNTRY].includes(totalKey.value))
            return [GROUPED_BY.RETAILER, GROUPED_BY.COUNTRY]
        else if ([GROUPED_BY.BRAND, GROUPED_BY.COMPANY].includes(totalKey.value))
            return [GROUPED_BY.BRAND, GROUPED_BY.COMPANY]
        return []
    })

    const isClickedRemaining = ![GROUPED_BY.BRAND, GROUPED_BY.COMPANY].includes(shareKey.value)
    const { isLoading, currentData, previousData, isCountryAgg, totalTitle, shareTitle, handleDownload } = useBannerPercentageSearch(
        `${BACKEND_APIS.BANNERS}/percentage/wrapped/dynamic`,
        forceLoad.value,
        { splitKpi: [totalKey.value, shareKey.value] }
    )

    useEffect(() => {
        forceLoad.value = true
    }, [shareKey.value, totalKey.value])

    useEffect(() => {
        if (isLoading) {
            data.value = []
            isBannersPercentageDynamicLoading.value = true
        } else {
            if (forceLoad.value)
                forceLoad.value = false

            const prevDict = [...previousData].reduce((acc, item) => {
                acc[item.rowID] = {
                    ...item,
                    rows: item.rows.reduce((accRow, row) => {
                        accRow[row.labelID] = row
                        return accRow
                    }, {})
                }
                return acc
            }, {})

            data.value = currentData.map(item => {
                const prevItem = prevDict[item.rowID]?.rows || {}
                return {
                    ...item,
                    rows: item.rows.map(row => {
                        const share = row.share ?? null
                        const prevShare = prevItem[row.labelID]?.share ?? null
                        const diff = share !== null && prevShare !== null ? share - prevShare : null

                        if (!!row?.remainingData?.length) {
                            const prevRemaining = [...prevItem[row.labelID]?.remainingData || []].reduce((acc, rd) => {
                                acc[rd.labelID] = rd;
                                return acc
                            }, {})

                            const labelsID = []
                            row.remainingData = row.remainingData.map(rd => {
                                const labelID = rd.labelID
                                const remainingShare = rd.share ?? null
                                let prevRemainingShare = prevItem[labelID]?.share ?? null
                                if (prevRemainingShare === null)
                                    prevRemainingShare = prevRemaining[labelID]?.share ?? null
                                const remainingDiff = remainingShare !== null && prevRemainingShare !== null ? remainingShare - prevRemainingShare : null

                                if (isClickedRemaining)
                                    labelsID.push(labelID)

                                return {
                                    ...rd,
                                    prevShare: prevRemainingShare,
                                    diff: remainingDiff
                                }
                            })

                            if (labelsID.length) {
                                if (shareKey.value === GROUPED_BY.PAGE_PRODUCT_TYPE)
                                    row.pageProductTypeID = [...labelsID]
                                else if (shareKey.value === GROUPED_BY.PAGE_CATEGORY)
                                    row.pageCategoryID = [...labelsID]
                                else if (shareKey.value === GROUPED_BY.IMAGE_TYPE)
                                    row.imageTypeID = [...labelsID]
                            }
                        }

                        return {
                            ...row,
                            prevShare: prevShare,
                            diff
                        }
                    })
                }
            })
            isBannersPercentageDynamicLoading.value = false
        }
    }, [isLoading])

    const handleChangeTotalKey = useCallback(newValue => batch(() => {
        totalKey.value = newValue
        if ([GROUPED_BY.RETAILER, GROUPED_BY.COUNTRY].includes(newValue) && [GROUPED_BY.RETAILER, GROUPED_BY.COUNTRY].includes(shareKey.value))
            shareKey.value = GROUPED_BY.BRAND
        else if ([GROUPED_BY.COMPANY, GROUPED_BY.BRAND].includes(newValue) && [GROUPED_BY.COMPANY, GROUPED_BY.BRAND].includes(shareKey.value))
            shareKey.value = GROUPED_BY.RETAILER
    }), [])

    const handleChangeShareKey = useCallback(newValue => batch(() => {
        shareKey.value = newValue
        if ([GROUPED_BY.RETAILER, GROUPED_BY.COUNTRY].includes(newValue) && [GROUPED_BY.RETAILER, GROUPED_BY.COUNTRY].includes(totalKey.value))
            totalKey.value = GROUPED_BY.BRAND
        else if ([GROUPED_BY.COMPANY, GROUPED_BY.BRAND].includes(newValue) && [GROUPED_BY.COMPANY, GROUPED_BY.BRAND].includes(totalKey.value))
            totalKey.value = GROUPED_BY.RETAILER
    }), [])

    return {
        isLoading,
        isCountryAgg,
        data: data.value,
        totalKey: totalKey.value,
        shareKey: shareKey.value,
        excludeOptions: excludeOptions.value,
        totalTitle,
        shareTitle,
        isClickedRemaining,

        handleChangeTotalKey,
        handleChangeShareKey,
        handleDownload
    }
}
